import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../i18n/LanguageContext';
import { destinations } from '../data/destinations';
import { packages } from '../data/packages';
import { waLink } from '../config/site';
import { Reveal } from '../components/Reveal';

const inputCls =
  'w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 focus:border-gold focus:outline-none transition-colors duration-300';
const labelCls = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.25em] text-navy/60';

const PlanMyTrip = () => {
  const { lang, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const preDestination = searchParams.get('destination');
  const prePackage = packages.find((p) => p.id === searchParams.get('package'));

  const [form, setForm] = useState({
    name: '',
    whatsapp: '',
    destinations: preDestination ? [preDestination] : [],
    departure: '',
    returnDate: '',
    travelers: t.form.travelersOptions[0],
    hotel: t.form.hotelOptions[1],
    budget: t.form.budgetOptions[4],
    preferences: [],
    additional: prePackage ? `${t.form.prefillPackage}: ${prePackage.name[lang]}` : '',
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const buildMessage = () => {
    const destNames = form.destinations.map((slug) => destinations.find((d) => d.slug === slug)?.name[lang]).filter(Boolean);
    const L = lang === 'id'
      ? {
          greet: 'Halo YOELI CHAN JOURNEY! Saya ingin merencanakan perjalanan privat.',
          name: 'Nama', wa: 'WhatsApp', dest: 'Destinasi', dep: 'Tanggal Berangkat', ret: 'Tanggal Kembali',
          pax: 'Jumlah Wisatawan', hotel: 'Kategori Hotel', budget: 'Estimasi Budget per Orang',
          pref: 'Preferensi Perjalanan', extra: 'Permintaan Tambahan', close: 'Mohon dibuatkan proposal perjalanan saya. Terima kasih!',
        }
      : {
          greet: 'Hello YOELI CHAN JOURNEY! I would like to plan a private trip.',
          name: 'Name', wa: 'WhatsApp', dest: 'Destination', dep: 'Departure Date', ret: 'Return Date',
          pax: 'Number of Travelers', hotel: 'Hotel Category', budget: 'Estimated Budget per Person',
          pref: 'Travel Preferences', extra: 'Additional Requests', close: 'Please prepare my custom travel proposal. Thank you!',
        };
    const lines = [
      L.greet, '',
      `${L.name}: ${form.name}`,
      `${L.wa}: ${form.whatsapp}`,
      `${L.dest}: ${destNames.join(', ')}`,
    ];
    if (form.departure) lines.push(`${L.dep}: ${form.departure}`);
    if (form.returnDate) lines.push(`${L.ret}: ${form.returnDate}`);
    lines.push(`${L.pax}: ${form.travelers}`);
    lines.push(`${L.hotel}: ${form.hotel}`);
    lines.push(`${L.budget}: ${form.budget}`);
    if (form.preferences.length) lines.push(`${L.pref}: ${form.preferences.join(', ')}`);
    if (form.additional) lines.push(`${L.extra}: ${form.additional}`);
    lines.push('', L.close);
    return lines.join('\n');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim() || form.destinations.length === 0) {
      toast.error(t.form.required);
      return;
    }
    toast.success(t.form.successTitle, { description: t.form.successDesc });
    window.open(waLink(buildMessage()), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-ivory" data-testid="plan-my-trip-page">
      <section className="mx-auto max-w-4xl px-5 pb-28 pt-36 md:px-8 md:pt-44">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.form.kicker}</p>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-navy md:text-6xl">{t.form.title}</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy/60">{t.form.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={handleSubmit} className="mt-14 flex flex-col gap-10 border border-navy/10 bg-beige/50 p-6 md:p-12" data-testid="trip-inquiry-form">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <label htmlFor="full-name" className={labelCls}>{t.form.fullName} *</label>
                <input id="full-name" data-testid="form-full-name" className={inputCls} placeholder={t.form.fullNamePh}
                  value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div>
                <label htmlFor="whatsapp-number" className={labelCls}>{t.form.whatsapp} *</label>
                <input id="whatsapp-number" data-testid="form-whatsapp" className={inputCls} placeholder={t.form.whatsappPh}
                  value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
              </div>
            </div>

            <div>
              <span className={labelCls}>{t.form.destination} *</span>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="form-destinations">
                {destinations.map((d) => {
                  const active = form.destinations.includes(d.slug);
                  return (
                    <button
                      type="button"
                      key={d.slug}
                      data-testid={`form-destination-${d.slug}`}
                      onClick={() => toggle('destinations', d.slug)}
                      className={`flex items-center justify-between border px-4 py-3 text-sm transition-colors duration-300 ${
                        active ? 'border-gold bg-navy text-ivory' : 'border-navy/20 bg-white text-navy/70 hover:border-navy/50'
                      }`}
                    >
                      {d.name[lang]}
                      {active && <Check className="h-4 w-4 text-gold" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <label htmlFor="departure-date" className={labelCls}>{t.form.departure}</label>
                <input id="departure-date" type="date" data-testid="form-departure-date" className={inputCls}
                  value={form.departure} onChange={(e) => set('departure', e.target.value)} />
              </div>
              <div>
                <label htmlFor="return-date" className={labelCls}>{t.form.return}</label>
                <input id="return-date" type="date" data-testid="form-return-date" className={inputCls}
                  value={form.returnDate} onChange={(e) => set('returnDate', e.target.value)} />
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <label htmlFor="travelers" className={labelCls}>{t.form.travelers}</label>
                <select id="travelers" data-testid="form-travelers" className={inputCls}
                  value={form.travelers} onChange={(e) => set('travelers', e.target.value)}>
                  {t.form.travelersOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="hotel-category" className={labelCls}>{t.form.hotel}</label>
                <select id="hotel-category" data-testid="form-hotel" className={inputCls}
                  value={form.hotel} onChange={(e) => set('hotel', e.target.value)}>
                  {t.form.hotelOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div>
              <span className={labelCls}>{t.form.budget}</span>
              <div className="flex flex-wrap gap-2" data-testid="form-budget">
                {t.form.budgetOptions.map((o) => (
                  <button
                    type="button"
                    key={o}
                    data-testid={`form-budget-${t.form.budgetOptions.indexOf(o)}`}
                    onClick={() => set('budget', o)}
                    className={`border px-4 py-2.5 text-sm transition-colors duration-300 ${
                      form.budget === o ? 'border-gold bg-gold text-navy' : 'border-navy/20 bg-white text-navy/70 hover:border-navy/50'
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className={labelCls}>{t.form.preferences}</span>
              <div className="flex flex-wrap gap-2" data-testid="form-preferences">
                {t.form.preferencesOptions.map((o) => {
                  const active = form.preferences.includes(o);
                  return (
                    <button
                      type="button"
                      key={o}
                      data-testid={`form-preference-${t.form.preferencesOptions.indexOf(o)}`}
                      onClick={() => toggle('preferences', o)}
                      className={`border px-4 py-2.5 text-sm transition-colors duration-300 ${
                        active ? 'border-gold bg-navy text-ivory' : 'border-navy/20 bg-white text-navy/70 hover:border-navy/50'
                      }`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="additional-requests" className={labelCls}>{t.form.additional}</label>
              <textarea id="additional-requests" rows={4} data-testid="form-additional" className={`${inputCls} resize-none`}
                placeholder={t.form.additionalPh} value={form.additional} onChange={(e) => set('additional', e.target.value)} />
            </div>

            <button
              type="submit"
              data-testid="form-submit-button"
              className="group inline-flex w-fit items-center gap-3 bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
            >
              {t.form.submit}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
};

export default PlanMyTrip;
